import React, { useState } from 'react';
import { IoMdClose } from 'react-icons/io';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import toast from 'react-hot-toast';

import { API_URL } from '../../services/api.js';

const RazorpayAuthModal = ({ userGoogleId, onClose, onSuccess }) => {
  const [keyId, setKeyId] = useState('');
  const [keySecret, setKeySecret] = useState('');
  const [showKeySecret, setShowKeySecret] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!keyId || !keySecret) {
      toast.error('Please enter both Key ID and Key Secret.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/auth/app/razorpay/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userGoogleId}`,
        },
        body: JSON.stringify({ key_id: keyId, key_secret: keySecret }),
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok || data.error) {
        throw new Error(data.error || data.detail || 'Authentication failed.');
      }

      toast.success('Razorpay connected successfully!');
      onSuccess(); // Close modal and refresh list
    } catch (error) {
      toast.error(error.message || 'Failed to connect to Razorpay.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
      {/* eslint-disable-next-line */}
      <div
        className="bg-2 relative flex w-full max-w-md flex-col rounded-lg border border-zinc-700 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-zinc-200">
            Connect to Razorpay
          </h2>
          <button
            onClick={onClose}
            className="text-zinc-500 transition-colors hover:text-white"
          >
            <IoMdClose size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <label
              htmlFor="key-id"
              className="text-sm font-medium text-zinc-300"
            >
              Key ID
            </label>
            <input
              type="text"
              placeholder="Enter your Razorpay Key ID"
              value={keyId}
              onChange={(e) => setKeyId(e.target.value)}
              className="rounded-md border border-zinc-600 bg-zinc-800 p-3 text-white placeholder-zinc-500 transition-colors focus:border-blue-500 focus:outline-none"
              required
            />
          </div>

          <div className="relative flex flex-col gap-2">
            <label
              htmlFor="key-secret"
              className="text-sm font-medium text-zinc-300"
            >
              Key Secret
            </label>
            <div className="relative">
              <input
                type={showKeySecret ? 'text' : 'password'}
                placeholder="Enter your Razorpay Key Secret"
                value={keySecret}
                onChange={(e) => setKeySecret(e.target.value)}
                className="w-full rounded-md border border-zinc-600 bg-zinc-800 p-3 pr-10 text-white placeholder-zinc-500 transition-colors focus:border-blue-500 focus:outline-none"
                required
              />
              <button
                type="button"
                onClick={() => setShowKeySecret(!showKeySecret)}
                className="absolute right-3 top-1/2 -translate-y-1/2 transform text-zinc-400 transition-colors hover:text-white"
              >
                {showKeySecret ? <FiEyeOff size={18} /> : <FiEye size={18} />}
              </button>
            </div>
            <p className="mt-1 text-xs text-zinc-500">
              Generate API credentials from your Razorpay account settings.
              <br />
              Go to Settings → API Keys to create new credentials.
            </p>
          </div>

          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="mr-3 px-4 py-2 text-zinc-300 transition-colors hover:text-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !keyId || !keySecret}
              className={`rounded-md px-6 py-2 font-medium text-white transition-colors ${loading ? 'cursor-not-allowed bg-blue-800' : 'bg-blue-600 hover:bg-blue-700'}`}
            >
              {loading ? 'Connecting...' : 'Connect'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RazorpayAuthModal;
